import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * Scroll reveals hide their content until an intersection is reported. Where
 * that can't happen, skip the animation entirely rather than risk a blank page.
 */
const SUPPORTS_IO = typeof IntersectionObserver !== 'undefined';

/** Fades + lifts its children the first time they scroll into view. */
export function Reveal({
  children,
  delay = 0,
  y = 22,
  className,
  onMount = false,
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  /** Play immediately instead of on scroll. Use for anything above the fold. */
  onMount?: boolean;
}) {
  const reduce = useReducedMotion();

  // If the browser can't report intersections, never hide the content.
  if (reduce || !SUPPORTS_IO) return <div className={className}>{children}</div>;

  const target = { opacity: 1, y: 0 };
  const transition = { duration: 0.75, delay, ease: EASE };

  if (onMount) {
    return (
      <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        animate={target}
        transition={transition}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={target}
      viewport={{ once: true, margin: '-12% 0px -12% 0px' }}
      transition={transition}
    >
      {children}
    </motion.div>
  );
}

/**
 * Display type that wipes up from behind a mask, one line at a time.
 * Each entry in `lines` is rendered as its own clipped row.
 */
export function MaskedLines({
  lines,
  className,
  delay = 0,
  stagger = 0.08,
  onMount = false,
}: {
  lines: React.ReactNode[];
  className?: string;
  delay?: number;
  stagger?: number;
  /** Play immediately instead of on scroll. Use for anything above the fold. */
  onMount?: boolean;
}) {
  const reduce = useReducedMotion();

  if (reduce || !SUPPORTS_IO) {
    return (
      <span className={className}>
        {lines.map((line, i) => (
          <span className="pu-mask" key={i}>
            <span className="block">{line}</span>
          </span>
        ))}
      </span>
    );
  }

  return (
    <span className={className}>
      {lines.map((line, i) => {
        const transition = { duration: 0.95, delay: delay + i * stagger, ease: EASE };
        return (
          <span className="pu-mask" key={i}>
            {onMount ? (
              <motion.span
                className="block"
                initial={{ y: '120%' }}
                animate={{ y: '0%' }}
                transition={transition}
              >
                {line}
              </motion.span>
            ) : (
              <motion.span
                className="block"
                initial={{ y: '120%' }}
                whileInView={{ y: '0%' }}
                viewport={{ once: true, margin: '-8% 0px' }}
                transition={transition}
              >
                {line}
              </motion.span>
            )}
          </span>
        );
      })}
    </span>
  );
}

/**
 * Eases a number towards its target with rAF. Used for the slip maths and
 * the stat rail so figures settle rather than snap.
 */
export function useEasedNumber(target: number, duration = 620) {
  const reduce = useReducedMotion();
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (reduce) {
      setValue(target);
      return;
    }

    const from = fromRef.current;
    if (from === target) return;

    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      // easeOutExpo
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      const next = from + (target - from) * eased;
      setValue(next);
      fromRef.current = next;

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, reduce]);

  return value;
}

/** Counts up to `to` once it enters the viewport. */
export function CountUp({
  to,
  decimals = 0,
  prefix = '',
  suffix = '',
  duration = 1400,
  className,
}: {
  to: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px' });
  const reduce = useReducedMotion();
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setValue(to);
      return;
    }

    const start = performance.now();
    let raf: number;

    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
      setValue(to * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, reduce]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
}

/** Small mono caption with a leading index, e.g. `[ 02 ] WHERE THE MONEY SITS`. */
export function SectionLabel({ index, children }: { index: string; children: React.ReactNode }) {
  return (
    <div className="pu-label flex items-center gap-3">
      <span style={{ color: 'var(--acid)' }}>[ {index} ]</span>
      <span>{children}</span>
    </div>
  );
}
